/**
 * TemplatePicker.tsx — the mockup's "Choose up to 3 templates" picker.
 *
 * Two tabs over the ranked candidates from `/slides/transform-retrieve`:
 *
 *   Best matches — the top {@link BEST_MATCH_COUNT}, each with its match %.
 *   More layouts — everything else the ranker returned, searchable, with
 *                  favourites.
 *
 * The mockup labels the second tab "Browse all layouts" over the whole library.
 * That is not offered here on purpose: nothing serves the full catalog to the
 * client (`/transform-retrieve` returns a ranked shortlist), so a tab claiming
 * to browse all ~1,400 layouts would be a promise the data can't keep. The
 * two-tier split the mockup depends on is kept — and so is the consequence it
 * attaches to it: a pick from the lower tier was not auto-matched, so it fits
 * with `"generic"`, exactly as the mockup forces "AI rewrite" for browse picks.
 *
 * Selection is ordered: the badge on each card is its pick number, and the
 * variants are inserted in that order.
 */

import { useCallback, useMemo, useState } from "react";
import { LayoutThumbnail } from "./LayoutThumbnail";
import { MAX_TEMPLATE_PICKS } from "../hooks/useTemplateTransform";
import { BEST_MATCH_COUNT } from "../services/templateFitPolicy";
import type {
  TemplateCandidate,
  TemplateFitMode,
} from "../services/slideTransformTemplateApi";
import styles from "./TemplatePicker.module.css";

// Re-exported so the picker stays the one import for surfaces that render it
// and also need the tier boundary; the rule itself lives in templateFitPolicy.
export { BEST_MATCH_COUNT };

const PERCENT = 100;

type PickerTab = "matched" | "more";

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
    </svg>
  );
}

/** "3-column" → "3 column"; the raw class is jargon in a card footer. */
function layoutLabel(candidate: TemplateCandidate): string {
  const layout = candidate.layoutClass?.replace(/-/g, " ") ?? "content";
  return layout.replace("6plus", "6+");
}

/** A template's display name — the `.pptx` stem, which is how authors name layouts. */
function templateName(candidate: TemplateCandidate): string {
  const file = candidate.pptxFile ?? candidate.id;
  return file.replace(/\.pptx$/i, "").replace(/^.*\//, "");
}

export interface TemplatePickerProps {
  readonly candidates: readonly TemplateCandidate[];
  /** Picked template ids, in pick order. */
  readonly selected: readonly string[];
  readonly onToggle: (templateId: string) => void;
  readonly fitMode: TemplateFitMode;
  readonly onFitModeChange: (mode: TemplateFitMode) => void;
  /**
   * True when a pick came from the lower tier, which forces `"generic"` — the
   * fill-mode control is locked and explains why.
   */
  readonly fitModeLocked: boolean;
}

export function TemplatePicker({
  candidates,
  selected,
  onToggle,
  fitMode,
  onFitModeChange,
  fitModeLocked,
}: Readonly<TemplatePickerProps>) {
  const [tab, setTab] = useState<PickerTab>("matched");
  const [query, setQuery] = useState("");
  const [favourites, setFavourites] = useState<readonly string[]>([]);
  const [favouritesOnly, setFavouritesOnly] = useState(false);

  const { matched, more } = useMemo(
    () => ({
      matched: candidates.slice(0, BEST_MATCH_COUNT),
      more: candidates.slice(BEST_MATCH_COUNT),
    }),
    [candidates],
  );

  const visible = useMemo(() => {
    if (tab === "matched") return matched;
    const needle = query.trim().toLowerCase();
    return more.filter((candidate) => {
      if (favouritesOnly && !favourites.includes(candidate.id)) return false;
      if (!needle) return true;
      // Searchable on the things a user would actually type: the layout's name,
      // its class ("3 column") and its intent tags ("comparison").
      const haystack = [
        templateName(candidate),
        layoutLabel(candidate),
        ...(candidate.intentTags ?? []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [tab, matched, more, query, favourites, favouritesOnly]);

  const toggleFavourite = useCallback((templateId: string) => {
    setFavourites((previous) =>
      previous.includes(templateId)
        ? previous.filter((id) => id !== templateId)
        : [...previous, templateId],
    );
  }, []);

  const atCap = selected.length >= MAX_TEMPLATE_PICKS;

  return (
    <div className={styles.picker}>
      <p className={styles.lede}>
        {tab === "matched"
          ? `Ranked against your slide's structure — % is match confidence. Pick up to ${MAX_TEMPLATE_PICKS} and you'll get one variant per pick to compare.`
          : "Layouts that ranked lower for this slide. Picking one re-shapes your content to suit it."}
      </p>

      <div className={styles.tabs} role="tablist" aria-label="Template source">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "matched"}
          className={`${styles.tab} ${tab === "matched" ? styles.tabOn : ""}`}
          onClick={() => setTab("matched")}
        >
          Best matches
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "more"}
          className={`${styles.tab} ${tab === "more" ? styles.tabOn : ""}`}
          onClick={() => setTab("more")}
          disabled={more.length === 0}
          title={more.length === 0 ? "No further layouts were returned for this slide" : undefined}
        >
          More layouts{more.length > 0 ? ` (${more.length})` : ""}
        </button>
      </div>

      {tab === "more" ? (
        <div className={styles.toolbar}>
          <input
            type="search"
            className={styles.search}
            placeholder="Search layouts — '3 column', 'timeline'…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search layouts"
          />
          <button
            type="button"
            className={`${styles.favFilter} ${favouritesOnly ? styles.favFilterOn : ""}`}
            aria-pressed={favouritesOnly}
            // The button's visible content is an icon plus a bare count, which
            // reads as just a number to a screen reader.
            aria-label={`Show favourites only (${favourites.length})`}
            onClick={() => setFavouritesOnly((on) => !on)}
          >
            <StarIcon />
            <span className={styles.favCount}>{favourites.length}</span>
          </button>
        </div>
      ) : null}

      <div className={styles.grid}>
        {visible.map((candidate) => {
          const pickIndex = selected.indexOf(candidate.id);
          const isSelected = pickIndex >= 0;
          const isFavourite = favourites.includes(candidate.id);
          const name = templateName(candidate);
          const percent =
            typeof candidate.score === "number" ? Math.round(candidate.score * PERCENT) : null;

          return (
            // The favourite toggle is a SIBLING of the card rather than a child:
            // a <button> inside a <button> is invalid HTML, which is why it used
            // to be a role="button" span — a control assistive tech announced
            // but the browser never treated as one. This wrapper is the grid
            // item and the positioning context, so the star still sits over the
            // thumbnail and the card keeps native button semantics.
            <div key={candidate.id} className={styles.cardWrap}>
              <button
                type="button"
                className={`${styles.card} ${isSelected ? styles.cardOn : ""}`}
                onClick={() => onToggle(candidate.id)}
                // Cards past the cap stay visible but inert, so the limit reads as
                // a limit instead of a click that does nothing.
                disabled={!isSelected && atCap}
                aria-pressed={isSelected}
                title={
                  !isSelected && atCap
                    ? `Deselect one first — up to ${MAX_TEMPLATE_PICKS} templates.`
                    : name
                }
              >
                {isSelected ? <span className={styles.badge}>{pickIndex + 1}</span> : null}

                <LayoutThumbnail
                  layoutClass={candidate.layoutClass}
                  className={styles.thumb}
                  label={`${name} — ${layoutLabel(candidate)} layout`}
                />

                <span className={styles.cardBody}>
                  <span className={styles.cardName}>{name}</span>
                  {percent !== null && tab === "matched" ? (
                    <span className={styles.score}>{percent}%</span>
                  ) : null}
                </span>
                <span className={styles.cardMeta}>{layoutLabel(candidate)}</span>
              </button>

              {tab === "more" ? (
                <button
                  type="button"
                  className={`${styles.star} ${isFavourite ? styles.starOn : ""}`}
                  aria-label={isFavourite ? `Unfavourite ${name}` : `Favourite ${name}`}
                  aria-pressed={isFavourite}
                  onClick={() => toggleFavourite(candidate.id)}
                >
                  <StarIcon />
                </button>
              ) : null}
            </div>
          );
        })}

        {visible.length === 0 ? (
          <p className={styles.empty}>
            {favouritesOnly
              ? "No favourites yet — tap the star on a layout to save it here."
              : "No layouts match that search."}
          </p>
        ) : null}
      </div>

      <div className={styles.footer}>
        <span className={styles.fillModeLabel}>How should the text be fitted?</span>
        <div className={styles.fillMode} role="radiogroup" aria-label="How the text is fitted">
          <button
            type="button"
            role="radio"
            aria-checked={fitMode === "generic"}
            className={`${styles.fillOption} ${fitMode === "generic" ? styles.fillOptionOn : ""}`}
            onClick={() => onFitModeChange("generic")}
          >
            <span className={styles.fillTitle}>AI rewrite</span>
            <span className={styles.fillHint}>reshapes to fit each slot</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={fitMode === "preserve"}
            className={`${styles.fillOption} ${fitMode === "preserve" ? styles.fillOptionOn : ""}`}
            onClick={() => onFitModeChange("preserve")}
            disabled={fitModeLocked}
            title={
              fitModeLocked
                ? "A layout from More layouts wasn't matched to this slide, so its content is always rewritten to fit."
                : undefined
            }
          >
            <span className={styles.fillTitle}>Keep my wording</span>
            <span className={styles.fillHint}>closer to the original</span>
          </button>
        </div>

        <span className={`${styles.counter} ${selected.length > 0 ? styles.counterActive : ""}`}>
          {selected.length} of {MAX_TEMPLATE_PICKS} selected
        </span>
      </div>
    </div>
  );
}
